import React, { useEffect, useMemo, useState } from "react";
import dayjs from "dayjs";
import RecommendedSchedulePlanner from "./RecommendedSchedulePlanner.jsx";
import RecommendedScheduleView from "./RecommendedScheduleView.jsx";
import { hrqGetRange } from "./harvestRequirement.storage.js";
import { BACKEND_DATE_FORMAT } from "../../../consts/index.js";

/** @typedef {import("./harvestOptimizer.types.d.ts").RecommendedHarvestScheduleProps} RecommendedHarvestScheduleProps */

const toISO = (dateObj) => dateObj.format(BACKEND_DATE_FORMAT);

const buildWeekMondayToSunday = (anchorDate) => {
  const dayOfWeek = anchorDate.day();
  const monday = anchorDate.subtract((dayOfWeek + 6) % 7, "day").startOf("day");
  return Array.from({ length: 7 }, (_, index) => monday.add(index, "day"));
};

const buildInclusiveDates = (startISO, endISO) => {
  const start = dayjs(startISO).startOf("day");
  const end = dayjs(endISO).startOf("day");
  const days = [];
  for (
    let cursor = start;
    !cursor.isAfter(end, "day");
    cursor = cursor.add(1, "day")
  ) {
    days.push(cursor);
  }
  return days;
};

const normalizeDayName = (name) => String(name || "").toLowerCase();

const pickFirstDatesByVisibleDays = (dateObjs, visibleDays) => {
  const requested = new Set((visibleDays || []).map(normalizeDayName));
  const seen = new Set();
  const out = [];

  for (const d of dateObjs) {
    const name = normalizeDayName(d.format("dddd"));
    if (!requested.has(name)) continue;
    if (seen.has(name)) continue;
    seen.add(name);
    out.push({ nameOriginal: name, dateObj: d });
  }
  return out;
};

const getSiteId = (currentSite) =>
  typeof currentSite === "string" ? currentSite : currentSite?.id || "site";

const DEFAULT_DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"];
const OPTIONAL_DAYS = ["Saturday", "Sunday"];

/** @param {RecommendedHarvestScheduleProps} props */
export default function RecommendedHarvestSchedule({
  messageApi,
  currentOrg,
  currentSite,
  currentCrop,
  siteMetadata,
  greenHouseLabel,
  blockLabel,
}) {
  const [selectedDate, setSelectedDate] = useState(dayjs().startOf("day"));
  const [dateRange, setDateRange] = useState(null);

  const [refreshKey, setRefreshKey] = useState(0);
  const [configLoading, setConfigLoading] = useState(false);
  const handleRefresh = () => setRefreshKey((v) => v + 1);

  const siteId = getSiteId(currentSite);

  const dateObjsWindow = useMemo(() => {
    if (dateRange?.startISO && dateRange?.endISO) {
      return buildInclusiveDates(dateRange.startISO, dateRange.endISO);
    }
    return buildWeekMondayToSunday(selectedDate);
  }, [selectedDate, dateRange]);

  const [startISO, endISO] = useMemo(() => {
    if (!dateObjsWindow.length) return [null, null];
    const first = dateObjsWindow[0];
    const last = dateObjsWindow[dateObjsWindow.length - 1];
    return [toISO(first), toISO(last)];
  }, [dateObjsWindow]);

  const [visibleDaysRowHarvestMap, setVisibleDaysRowHarvestMap] = useState({});

  useEffect(() => {
    let alive = true;
    (async () => {
      if (!startISO || !endISO || !siteId) {
        if (alive) {
          setVisibleDaysRowHarvestMap({});
          setConfigLoading(false);
        }
        return;
      }

      setConfigLoading(true);
      try {
        const allWeekDates = dateObjsWindow;
        const existing = await hrqGetRange(siteId, startISO, endISO); // { iso: {type,value} }

        const isValidEntry = (e) =>
          !!e &&
          typeof e === "object" &&
          e.type != null &&
          e.value != null &&
          Number.isFinite(Number(e.value));

        const fullDays = [...DEFAULT_DAYS, ...OPTIONAL_DAYS];
        const chosenDefault = pickFirstDatesByVisibleDays(
          allWeekDates,
          fullDays,
        );
        const fullDayMap = {};

        for (const { nameOriginal, dateObj } of chosenDefault) {
          const label =
            fullDays.find((v) => normalizeDayName(v) === nameOriginal) ||
            dateObj.format("dddd");
          const iso = toISO(dateObj);
          const e = existing[iso];
          if (!isValidEntry(e)) continue;
          fullDayMap[label] = { type: String(e.type), value: Number(e.value) };
        }
        if (alive) setVisibleDaysRowHarvestMap(fullDayMap);
      } finally {
        if (alive) setConfigLoading(false);
      }
    })();
    // depend on refreshKey to force refetch
    return () => {
      alive = false;
    };
  }, [dateObjsWindow, startISO, endISO, siteId, refreshKey]);

  return (
    <div className="w-full">
      <RecommendedSchedulePlanner
        currentOrg={currentOrg}
        currentSite={currentSite}
        currentCrop={currentCrop}
        selectedDate={selectedDate}
        dateRange={dateRange}
        visibleDaysConfig={visibleDaysRowHarvestMap}
        siteMetadata={siteMetadata}
        refreshKey={refreshKey}
      >
        {({ loading, error, items }) => (
          <RecommendedScheduleView
            currentOrgId={currentOrg}
            currentSite={currentSite}
            currentCrop={currentCrop}
            selectedDate={selectedDate}
            onDateChange={setSelectedDate}
            loading={loading}
            error={error}
            items={items}
            baseDays={DEFAULT_DAYS}
            optionalDays={OPTIONAL_DAYS}
            visibleDaysConfig={visibleDaysRowHarvestMap}
            greenHouseLabel={greenHouseLabel}
            blockLabel={blockLabel}
            onRangeChange={setDateRange}
            onRefresh={handleRefresh}
            refreshLoading={configLoading || loading}
            siteMetadata={siteMetadata}
          />
        )}
      </RecommendedSchedulePlanner>
    </div>
  );
}
